/**
 * Add people — pick Estimator accounts to attach to a retailer node.
 *
 * Trusscore admins can attach anyone, so the list is every account, but the
 * ones the matcher already points at this retailer float to the top with the
 * reason: their Salesforce account is mapped here, their email domain is this
 * retailer's, they came in on this retailer's invite code, or the matcher
 * listed this retailer as a candidate. Accounts already on the target are hidden.
 *
 * Adding gives "Entire <target>" access with the chosen role; narrow it
 * afterwards from the People tab.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, X, UserPlus, CheckSquare, Square, AlertTriangle, Sparkles } from 'lucide-react';
import { fetchAccounts, setAccountGrants, type AccountRow } from '../../../services/adminAccountsApi';
import { fetchRetailerMembers, type RetailerMemberRow, type RetailerMemberRole } from '../../../services/adminRetailerMembersApi';
import { fetchAdminRetailer } from '../../../services/adminRetailersApi';
import type { RetailerTreeNode } from '../../../services/adminRetailerTreeApi';
import { ROLE_LABEL, ROLE_HELP } from './PeoplePane';

interface Props {
  node: RetailerTreeNode;
  /** The node and its sub-retailers (targets offered). */
  family: RetailerTreeNode[];
  onClose: () => void;
  onAdded: (summary: { added: number; failed: string[] }) => void;
}

interface Candidate {
  account: AccountRow;
  score: number;
  reasons: string[];
  /** Where they already are (other retailers). */
  elsewhere: string[];
}

export const AddPeopleModal: React.FC<Props> = ({ node, family, onClose, onAdded }) => {
  const [targetId, setTargetId] = useState(node.id);
  const [role, setRole] = useState<RetailerMemberRole>('viewer');
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [members, setMembers] = useState<RetailerMemberRow[]>([]);
  const [domains, setDomains] = useState<Map<number, string[]>>(new Map());
  const [sfIds, setSfIds] = useState<Map<number, string[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);

  const familyIds = useMemo(() => new Set(family.map(n => n.id)), [family]);

  // Who the retailer family claims: email domains + Salesforce accounts, per node.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, ...rs] = await Promise.all([fetchRetailerMembers('all'), ...family.map(n => fetchAdminRetailer(n.id).catch(() => null))]);
        if (cancelled) return;
        setMembers(m.members);
        const d = new Map<number, string[]>(); const s = new Map<number, string[]>();
        rs.forEach((r, i) => {
          if (!r) return;
          d.set(family[i].id, (r.retailer.emailDomains ?? []).map(x => x.toLowerCase()));
          s.set(family[i].id, (r.retailer.sfAccounts ?? []).map(x => x.sfAccountId.slice(0, 15)));
        });
        setDomains(d); setSfIds(s);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load matches');
      }
    })();
    return () => { cancelled = true; };
  }, [family]);

  // The accounts themselves (server search; 200 most recently active without a term).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetchAccounts({ search: search.trim() || undefined, perPage: 200 });
        if (!cancelled) setAccounts(r.accounts);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load accounts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !adding) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, adding]);

  const target = family.find(n => n.id === targetId) ?? node;
  const allDomains = useMemo(() => new Set(Array.from(domains.values()).flat()), [domains]);
  const allSf = useMemo(() => new Set(Array.from(sfIds.values()).flat()), [sfIds]);

  const candidates = useMemo<Candidate[]>(() => {
    const rowsByUser = new Map<number, RetailerMemberRow[]>();
    for (const m of members) { const l = rowsByUser.get(m.userId) ?? []; l.push(m); rowsByUser.set(m.userId, l); }
    const out: Candidate[] = [];
    for (const a of accounts) {
      const rows = rowsByUser.get(a.id) ?? [];
      // Already on the target (any live status) → not offered.
      if (rows.some(r => r.retailerId === target.id && r.status !== 'rejected')) continue;
      let score = 0; const reasons: string[] = [];
      const emailDomain = a.email.split('@')[1]?.toLowerCase() ?? '';
      for (const r of rows) {
        if (r.sfAccountId && allSf.has(r.sfAccountId.slice(0, 15))) { score = Math.max(score, 4); reasons.push(`Salesforce account “${r.sfAccountName ?? r.sfAccountId}”`); }
        else if (r.sfAccountName && r.status === 'unmatched') { score = Math.max(score, 1); reasons.push(`Salesforce account “${r.sfAccountName}” (not mapped to any retailer)`); }
        if (r.matchedDomain && allDomains.has(r.matchedDomain.toLowerCase())) { score = Math.max(score, 3); reasons.push(`@${r.matchedDomain}`); }
        if (r.inviteCode && r.retailerId !== null && familyIds.has(r.retailerId)) { score = Math.max(score, 2); reasons.push(`invite code ${r.inviteCode}`); }
        if (r.candidates.some(c => familyIds.has(c.id))) { score = Math.max(score, 1); reasons.push('listed as a possible match'); }
        if (r.retailerId !== null && familyIds.has(r.retailerId) && r.retailerId !== target.id && r.status !== 'rejected') { score = Math.max(score, 1); reasons.push(`already on ${r.retailerName}`); }
      }
      if (score < 3 && emailDomain && allDomains.has(emailDomain)) { score = Math.max(score, 3); reasons.push(`@${emailDomain}`); }
      const elsewhere = a.memberships.filter(m => m.status === 'approved' && m.retailerId !== null && !familyIds.has(m.retailerId)).map(m => m.retailerName ?? `#${m.retailerId}`);
      out.push({ account: a, score, reasons: Array.from(new Set(reasons)), elsewhere });
    }
    return out.sort((x, y) => y.score - x.score || (x.account.lastName ?? '').localeCompare(y.account.lastName ?? '') || x.account.email.localeCompare(y.account.email));
  }, [accounts, members, target.id, allDomains, allSf, familyIds]);

  const suggested = candidates.filter(c => c.score > 0);
  const toggle = (id: number) => setTicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tickSuggested = () => setTicked(new Set(suggested.map(c => c.account.id)));

  const run = async () => {
    const ids = Array.from(ticked);
    if (!ids.length) return;
    setAdding({ done: 0, total: ids.length }); setError(null);
    let added = 0; const failed: string[] = [];
    for (let i = 0; i < ids.length; i++) {
      const a = accounts.find(x => x.id === ids[i]);
      try { await setAccountGrants({ userId: ids[i], retailerId: target.id, role, grants: [{ type: 'retailer', id: target.id, includeChildren: target.parentRetailerId === null }] }); added++; }
      catch (e) { failed.push(`${a?.email ?? ids[i]}: ${e instanceof Error ? e.message : 'failed'}`); }
      setAdding({ done: i + 1, total: ids.length });
    }
    onAdded({ added, failed });
  };

  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="addpeople-title">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center gap-2" id="addpeople-title"><UserPlus size={18} /> Add people to <strong>{target.name}</strong></h5>
              <button type="button" className="btn-close" aria-label="Close" disabled={!!adding} onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-2 align-items-end mb-3">
                <div className="col-12 col-md-5">
                  <label htmlFor="ap-search" className="form-label small fw-semibold mb-1">Find an account</label>
                  <div className="input-group">
                    <span className="input-group-text"><Search size={14} /></span>
                    <input id="ap-search" className="form-control" placeholder="Name or email…" value={search} onChange={e => setSearch(e.target.value)} autoComplete="off" />
                    {search && <button type="button" className="btn btn-outline-secondary" onClick={() => setSearch('')}><X size={13} /></button>}
                  </div>
                </div>
                {family.length > 1 && (
                  <div className="col-6 col-md-4">
                    <label htmlFor="ap-target" className="form-label small fw-semibold mb-1">Add to</label>
                    <select id="ap-target" className="form-select" value={targetId} onChange={e => { setTargetId(Number(e.target.value)); setTicked(new Set()); }}>
                      {family.map(n => <option key={n.id} value={n.id}>{n.id === node.id ? n.name : `↳ ${n.name}`}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-6 col-md-3">
                  <label htmlFor="ap-role" className="form-label small fw-semibold mb-1">As</label>
                  <select id="ap-role" className="form-select" value={role} title={ROLE_HELP[role]} onChange={e => setRole(e.target.value as RetailerMemberRole)}>
                    <option value="viewer">{ROLE_LABEL.viewer}</option>
                    <option value="manager">{ROLE_LABEL.manager}</option>
                  </select>
                </div>
              </div>
              {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}

              <div className="d-flex flex-wrap align-items-center gap-2 mb-2 small text-muted">
                <span>{candidates.length} account{candidates.length === 1 ? '' : 's'}{suggested.length ? <> · <span className="text-success fw-semibold">{suggested.length} look like {target.name}&apos;s</span></> : ''} · {ticked.size} ticked</span>
                {suggested.length > 0 && <button type="button" className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1" onClick={tickSuggested}><Sparkles size={13} /> Tick all {suggested.length} suggested</button>}
                {ticked.size > 0 && <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setTicked(new Set())}>Untick all</button>}
              </div>

              <div className="table-responsive border rounded" style={{ maxHeight: '50vh' }}>
                <table className="table table-sm table-hover align-middle mb-0 small">
                  <thead className="table-light position-sticky top-0">
                    <tr><th style={{ width: 32 }} /><th>Person</th><th>Why suggested</th><th>Already on</th><th>Last sign-in</th></tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan={5} className="text-center text-muted py-3"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</td></tr>
                      : candidates.length === 0 ? <tr><td colSpan={5} className="text-center text-muted py-3">{search ? 'No account matches.' : `Everyone is already on ${target.name}.`}</td></tr>
                      : candidates.map(c => {
                        const on = ticked.has(c.account.id);
                        return (
                          <tr key={c.account.id} className={on ? 'table-active' : c.score > 0 ? 'table-success' : ''} style={{ cursor: 'pointer' }} onClick={() => toggle(c.account.id)}>
                            <td>{on ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-muted" />}</td>
                            <td>
                              <div className="fw-semibold">{[c.account.firstName, c.account.lastName].filter(Boolean).join(' ') || <span className="text-muted">(no name)</span>}</div>
                              <div className="text-muted">{c.account.email}</div>
                            </td>
                            <td>{c.reasons.length ? c.reasons.map(r => <span key={r} className="badge text-bg-light border me-1">{r}</span>) : <span className="text-muted">—</span>}</td>
                            <td className="text-muted">{c.elsewhere.length ? c.elsewhere.join(', ') : '—'}</td>
                            <td className="text-muted">{c.account.lastLoginAt ? c.account.lastLoginAt.slice(0, 10) : 'never'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              <div className="form-text">Added people get access to the <strong>entire {target.name}</strong>; narrow it to specific locations afterwards from the People tab.</div>
            </div>
            <div className="modal-footer">
              {adding && <span className="small text-muted me-auto d-inline-flex align-items-center gap-1"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Adding {adding.done} / {adding.total}…</span>}
              <button type="button" className="btn btn-outline-secondary" disabled={!!adding} onClick={onClose}>Close</button>
              <button type="button" className="btn btn-primary" disabled={!!adding || ticked.size === 0} onClick={() => void run()}>Add {ticked.size || ''} as {ROLE_LABEL[role]}{ticked.size === 1 ? '' : 's'} to {target.name}</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

export default AddPeopleModal;
